/**
 * ServiceGrid — Zone-2 of the SUPER AWENI home page.
 *
 * Renders the 12 service "doors" from `doors.config.json` as a 4-column
 * grid (collapses to 3 on the smallest viewports). Each door is a NATIVE
 * <a href="#/route"> tag — never a JS-handler <div> — so:
 *   - INP stays ≤150ms (Google's "good" budget; we target <100ms)
 *   - Browser ctrl/cmd-click + middle-click + right-click open-in-new-tab
 *     all work natively
 *   - Screen readers + keyboard nav work without ARIA hacks
 *
 * Icons are inline SVGs from `icons.ts` (no external icon library = ~0
 * runtime cost). Color tints come from doors.config.json `color` field.
 *
 * Adding/removing/reordering doors: edit `doors.config.json` ONLY. This
 * component re-renders the next mount with the new list.
 */

import type { AweniContext } from '../../types';
import doorsConfig from './doors.config.json';
import { renderIcon } from './icons';

interface DoorEntry {
  id: string;
  labelKey: string;
  icon: string;
  color: string;
  route: string;
  minVersion?: string;
  category?: string;
  /**
   * `primary` doors render in the visible 4×2 grid above-the-fold.
   * `secondary` doors only show inside the More drawer (so the home
   * grid never exceeds the 8-tile cognitive-load threshold per the
   * super-app benchmark — Alipay 9-grid, WeChat 4×2 visible, etc.).
   */
  tier?: 'primary' | 'secondary';
}

interface DoorsConfig {
  _comment?: string;
  _version?: number;
  doors: DoorEntry[];
}

export class ServiceGrid {
  private root: HTMLElement | null = null;
  private ctx: AweniContext;

  constructor(ctx: AweniContext) {
    this.ctx = ctx;
  }

  /** Render synchronously into the given parent element. */
  mount(parent: HTMLElement): void {
    const config = doorsConfig as DoorsConfig;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const root = document.createElement('nav');
    root.className = 'aweni-service-grid';
    root.setAttribute('aria-label', t('home_grid_label') || 'AWENI services');
    this.root = root;

    // Render PRIMARY tier only — secondary tiles live in the More drawer.
    // Doors without `tier` default to primary for backward-compat.
    const primaries = config.doors.filter((d) => d.tier !== 'secondary');
    const visibleDoors = this.applyAnticipation(primaries);
    const items = visibleDoors.map((door, index) => {
      const label = t(door.labelKey) || door.id;
      // Use href="#/<route>" — hash router catches the change. Native <a>
      // means the browser handles click/touch synchronously: no JS handler
      // required, INP stays low, and screen readers announce as a link.
      const href = `#${door.route.startsWith('/') ? door.route : '/' + door.route}`;
      
      // Map configured hex color to corresponding system token or fall back to hex color
      let doorColorToken = door.color;
      switch (door.id) {
        case 'hisni': doorColorToken = 'var(--ln-accent-ceremonial, #C8A96A)'; break;
        case 'games': doorColorToken = 'var(--ln-color-blue-500, #3B82F6)'; break;
        case 'blog': doorColorToken = 'var(--ln-color-teal-500, #14B8A6)'; break;
        case 'selecto': doorColorToken = 'var(--ln-color-champagne-700, #F97316)'; break;
        case 'balancetab': doorColorToken = 'var(--ln-color-violet-500, #8B5CF6)'; break;
        case 'chat': doorColorToken = 'var(--ln-color-violet-600, #EC4899)'; break;
        case 'tools': doorColorToken = 'var(--ln-color-blue-500, #0EA5E9)'; break;
        case 'more': doorColorToken = 'var(--ln-color-midnight-500, #64748B)'; break;
        case 'family': doorColorToken = 'var(--ln-color-champagne-500, #F59E0B)'; break;
        case 'yawmi': doorColorToken = 'var(--ln-color-champagne-700, #8B6F3F)'; break;
        case 'community': doorColorToken = 'var(--ln-color-blue-500, #6366F1)'; break;
        case 'family-room': doorColorToken = 'var(--ln-color-violet-500, #F472B6)'; break;
        case 'weather': doorColorToken = 'var(--ln-color-blue-500, #0EA5E9)'; break;
        case 'watch': doorColorToken = 'var(--ln-color-violet-600, #EF4444)'; break;
        case 'profile': doorColorToken = 'var(--ln-color-midnight-500, #94A3B8)'; break;
      }
      
      const backgroundStyle = `color-mix(in srgb, ${doorColorToken} 10%, transparent)`;
      return `
        <a class="aweni-door" href="${href}"
           data-door-id="${door.id}"
           style="--i: ${index};"
           aria-label="${label}">
          <span class="aweni-door-icon-wrap" style="background:${backgroundStyle};color:${doorColorToken};">
            <span class="aweni-door-icon" style="transform: translateZ(8px); display: block;">
              ${renderIcon(door.icon, 28)}
            </span>
          </span>
          <span class="aweni-door-label">${label}</span>
        </a>`;
    }).join('');

    root.innerHTML = items;
    parent.appendChild(root);
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  /**
   * Re-order primary doors by hour-of-day intent. Pattern from Google
   * Discover / Samsung One UI / Naver: surface the door most likely to
   * be relevant first. Stable structure (`more` always last, never moves
   * spatially because the user's muscle memory expects it there).
   *
   * Primary doors: hisni, games, blog, selecto, balancetab, chat, tools, more
   * Rules (deterministic, no LLM):
   *   05–09 morning   → hisni, blog, games, balancetab, selecto, chat, tools, more
   *   09–12 mid-AM    → balancetab, hisni, games, blog, chat, selecto, tools, more
   *   12–14 lunch     → selecto, balancetab, hisni, games, blog, chat, tools, more
   *   14–18 afternoon → games, hisni, blog, selecto, balancetab, chat, tools, more
   *   18–21 evening   → games, selecto, hisni, blog, balancetab, chat, tools, more
   *   21–01 night     → hisni, blog, games, chat, selecto, balancetab, tools, more
   *   01–05 lateNight → hisni, blog, chat, balancetab, selecto, games, tools, more
   *   Friday boost: hisni floats to position 1 regardless of hour (Jumu'ah).
   *
   * Each door's *position* on the grid shifts by intent, but `more` is
   * always position 8 — muscle memory anchor (iOS Smart Stack pattern).
   */
  private applyAnticipation(doors: DoorEntry[]): DoorEntry[] {
    const now = new Date();
    const h = now.getHours();
    const isFriday = now.getDay() === 5;
    let preferred: string[];
    if (h >= 5 && h < 9)        preferred = ['hisni', 'blog', 'games', 'balancetab', 'selecto', 'chat', 'tools', 'more'];
    else if (h >= 9 && h < 12)  preferred = ['balancetab', 'hisni', 'games', 'blog', 'chat', 'selecto', 'tools', 'more'];
    else if (h >= 12 && h < 14) preferred = ['selecto', 'balancetab', 'hisni', 'games', 'blog', 'chat', 'tools', 'more'];
    else if (h >= 14 && h < 18) preferred = ['games', 'hisni', 'blog', 'selecto', 'balancetab', 'chat', 'tools', 'more'];
    else if (h >= 18 && h < 21) preferred = ['games', 'selecto', 'hisni', 'blog', 'balancetab', 'chat', 'tools', 'more'];
    else if (h >= 21 || h < 1)  preferred = ['hisni', 'blog', 'games', 'chat', 'selecto', 'balancetab', 'tools', 'more'];
    else                        preferred = ['hisni', 'blog', 'chat', 'balancetab', 'selecto', 'games', 'tools', 'more'];

    if (isFriday) {
      // Pull hisni to position 1 (Surah Al-Kahf + Jumu'ah adhkar).
      preferred = ['hisni', ...preferred.filter((id) => id !== 'hisni')];
    }

    const byId = new Map(doors.map((d) => [d.id, d]));
    const ordered: DoorEntry[] = [];
    for (const id of preferred) {
      const d = byId.get(id);
      if (d) { ordered.push(d); byId.delete(id); }
    }
    // Append any door not in the preferred list (forward-compat for new doors)
    for (const d of byId.values()) ordered.push(d);
    return ordered;
  }
}
